import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  Card, 
  Form, 
  Input, 
  InputNumber,
  Button, 
  Switch, 
  Select, 
  Space, 
  message, 
  Tabs, 
  Row, 
  Col,
  Divider,
  Typography,
  Alert,
  Tag,
  DatePicker,
  Statistic,
  Descriptions,
  Spin,
  Table,
  Checkbox,
  Modal,
} from 'antd'
import { ArrowLeftOutlined, SaveOutlined, ClockCircleOutlined, CalendarOutlined, LoginOutlined, CopyOutlined, LinkOutlined, StarOutlined, SendOutlined, UserOutlined, SyncOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { api } from '../services/api'
import BusinessUsersTab from '../components/BusinessUsersTab'

const { Title, Text, Paragraph } = Typography
const { Option } = Select
const { TextArea } = Input

const reviewLinkStorageKey = (businessId: string, userId: string) =>
  `admin-product-review-link:${businessId}:${userId}`

interface BusinessUser {
  id: string
  firstName: string
  lastName: string
  email: string
  role: string
  isActive: boolean
}

interface Business {
  id: string
  name: string
  email?: string
  phone?: string
  website?: string
  subscriptionPlan: string
  subscriptionStatus: string
  isActive: boolean
  users?: BusinessUser[]
}

interface ReviewRequestResult {
  token: string
  url: string
  expiresAt: string
  emailSent: boolean
  recipientEmail: string | null
}

interface BusinessSettings {
  stripeConfig?: {
    secretKey: string
    publishableKey: string
    webhookSecret?: string
    currency: string
    paymentMethodTypes: string[]
    terminalEnabled?: boolean
    terminalLocationId?: string
    companyName?: string
  }
  integrations?: {
    preferredSmsProvider?: 'telnyx' | 'twilio' | 'auto'
    stripe?: {
      secretKey: string
      publishableKey: string
      webhookSecret?: string
      currency: string
      paymentMethodTypes?: string[]
      terminalEnabled?: boolean
      terminalLocationId?: string
      companyName?: string
      isEnabled?: boolean
      connectedAt?: string
      achEnabled?: boolean
    }
    preferredCardProcessor?: 'stripe' | 'helcim' | 'finix' | 'banccard'
    helcim?: {
      apiToken: string
      webhookVerifierToken?: string
      currency?: string
      terminalId?: string
      environment?: 'test' | 'live'
      isEnabled?: boolean
      connectedAt?: string
    }
    twilio?: {
      accountSid: string
      authToken: string
      phoneNumber: string
      testPhoneNumber?: string
      apiKey?: string
      apiSecret?: string
      twimlAppSid?: string
      taskRouterWorkspaceSid?: string
      chatServiceSid?: string
    }
    telnyx?: {
      apiKey: string
      phoneNumber: string
      messagingProfileId?: string
    }
    mailgun?: {
      apiKey: string
      domain: string
      fromEmail: string
      region?: string
    }
    plaid?: {
      clientId: string
      secret: string
      environment: string
    }
    openai?: {
      apiKey: string
      organization?: string
      model?: string
      maxTokens?: string
      isEnabled?: boolean
      features?: {
        callMonitoring?: boolean
        emailAssistant?: boolean
        analytics?: boolean
      }
    }
  }
  notifications?: {
    email: boolean
    sms: boolean
    push: boolean
  }
  branding?: {
    primaryColor?: string
    secondaryColor?: string
    logo?: string
  }
  features?: {
    aiAssistant?: boolean
    advancedReporting?: boolean
    inventoryManagement?: boolean
    timeTracking?: boolean
  }
}

const BusinessSettingsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [business, setBusiness] = useState<Business | null>(null)
  const [settings, setSettings] = useState<BusinessSettings>({})
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm()
  const [rawJson, setRawJson] = useState<string>('')
  const [jsonError, setJsonError] = useState<string | null>(null)
  const [useRawJson, setUseRawJson] = useState(false)
  const [impersonating, setImpersonating] = useState(false)
  const [triggeringLateFees, setTriggeringLateFees] = useState(false)
  const [triggeringDigest, setTriggeringDigest] = useState(false)
  const [digestFrequency, setDigestFrequency] = useState<'daily' | 'weekly' | 'monthly'>('daily')
  const [digestUseCustomPeriod, setDigestUseCustomPeriod] = useState(false)
  const [digestCustomPeriod, setDigestCustomPeriod] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null)
  const [reviewUserId, setReviewUserId] = useState<string | undefined>()
  const [reviewSendEmail, setReviewSendEmail] = useState(false)
  const [reviewGenerating, setReviewGenerating] = useState(false)
  const [reviewResult, setReviewResult] = useState<ReviewRequestResult | null>(null)

  useEffect(() => {
    if (id) {
      fetchBusiness()
      fetchSettings()
    }
  }, [id])

  useEffect(() => {
    if (!id || !reviewUserId) {
      return
    }

    const storageKey = reviewLinkStorageKey(id, reviewUserId)
    const stored = sessionStorage.getItem(storageKey)
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as ReviewRequestResult
        if (parsed.url && parsed.expiresAt && new Date(parsed.expiresAt) > new Date()) {
          setReviewResult(parsed)
        } else {
          sessionStorage.removeItem(storageKey)
        }
      } catch {
        sessionStorage.removeItem(storageKey)
      }
    }

    const loadActiveReviewLink = async () => {
      try {
        const res = await api.get(`/businesses/${id}/review-request/active`, {
          params: { userId: reviewUserId },
        })
        if (res.data?.success && res.data?.data) {
          const activeResult: ReviewRequestResult = {
            ...res.data.data,
            emailSent: false,
            recipientEmail: null,
          }
          setReviewResult(activeResult)
          sessionStorage.setItem(storageKey, JSON.stringify(activeResult))
        }
      } catch (error: any) {
        if (error.response?.status !== 404) {
          console.error('Failed to load active review link:', error)
        }
      }
    }

    loadActiveReviewLink()
  }, [id, reviewUserId])

  useEffect(() => {
    if (!business?.users?.length || reviewUserId) {
      return
    }

    const defaultUser = business.users.find(
      (user) => user.isActive && user.role === 'OWNER'
    ) || business.users.find(
      (user) => user.isActive && user.role === 'ADMIN'
    )

    if (defaultUser) {
      setReviewUserId(defaultUser.id)
    }
  }, [business?.users, reviewUserId])

  // Sync raw JSON when settings change (from form)
  useEffect(() => {
    if (!useRawJson && settings && Object.keys(settings).length > 0) {
      try {
        const formatted = JSON.stringify(settings, null, 2)
        setRawJson(formatted)
        setJsonError(null)
      } catch (error) {
        console.error('Error formatting settings to JSON:', error)
      }
    }
  }, [settings, useRawJson])

  const fetchBusiness = async () => {
    try {
      const response = await api.get(`/businesses/${id}`)
      setBusiness(response.data)
    } catch (error) {
      console.error('Failed to fetch business:', error)
      message.error('Failed to load business information')
    }
  }

  const fetchSettings = async () => {
    setLoading(true)
    try {
      const response = await api.get(`/businesses/${id}/settings`)
      const fetchedSettings = response.data
      
      // Handle backward compatibility: if stripeConfig exists but integrations.stripe doesn't, migrate it
      if (fetchedSettings.stripeConfig && !fetchedSettings.integrations?.stripe) {
        if (!fetchedSettings.integrations) {
          fetchedSettings.integrations = {}
        }
        fetchedSettings.integrations.stripe = {
          ...fetchedSettings.stripeConfig,
          isEnabled: true,
          connectedAt: new Date().toISOString()
        }
        console.log('Migrated stripeConfig to integrations.stripe for backward compatibility')
      }
      
      setSettings(fetchedSettings)
      form.setFieldsValue(fetchedSettings)
      
      // Initialize raw JSON editor
      setRawJson(JSON.stringify(fetchedSettings, null, 2))
      setJsonError(null)
    } catch (error) {
      console.error('Failed to fetch settings:', error)
      message.error('Failed to load business settings')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async (values?: any) => {
    setSaving(true)
    try {
      let settingsToSave: any
      let replaceSettings = false

      // If raw JSON mode is enabled and JSON is valid, use raw JSON (full replace)
      if (useRawJson) {
        const validation = validateJson(rawJson)
        if (!validation.valid) {
          message.error('Invalid JSON. Please fix errors before saving.')
          setSaving(false)
          return
        }
        settingsToSave = validation.data
        replaceSettings = true
      } else {
        // Otherwise, use form values but ensure integrations.stripe is properly set
        settingsToSave = { ...values }
        
        // Ensure integrations.stripe exists if stripeConfig was provided (backward compatibility)
        if (values.stripeConfig && !values.integrations?.stripe) {
          if (!settingsToSave.integrations) {
            settingsToSave.integrations = {}
          }
          settingsToSave.integrations.stripe = {
            ...values.stripeConfig,
            isEnabled: true,
            connectedAt: new Date().toISOString()
          }
        }
        
        // Remove stripeConfig if integrations.stripe exists to avoid duplicates
        if (settingsToSave.integrations?.stripe && settingsToSave.stripeConfig) {
          delete settingsToSave.stripeConfig
        }
      }

      const payload = replaceSettings
        ? { ...settingsToSave, __replaceSettings: true }
        : settingsToSave

      await api.put(`/businesses/${id}/settings`, payload)
      message.success('Settings saved successfully')
      setSettings(settingsToSave)
      setUseRawJson(false) // Reset raw JSON mode after save
      
      // Reload settings to get the latest from server
      await fetchSettings()
    } catch (error) {
      console.error('Failed to save settings:', error)
      message.error('Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveClick = () => {
    if (useRawJson) {
      void handleSave()
      return
    }
    form.submit()
  }

  if (!business) {
    return <div>Loading...</div>
  }

  const stripeConfigTab = (
    <Card title="Stripe Configuration" style={{ marginBottom: '16px' }}>
      <Alert
        message="Stripe Integration"
        description="Configure Stripe payment processing for this business. These settings will override the default system configuration."
        type="info"
        style={{ marginBottom: '16px' }}
      />
      
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name={['integrations', 'stripe', 'secretKey']}
            label="Secret Key"
            rules={[{ pattern: /^sk_/, message: 'Secret key must start with sk_' }]}
          >
            <Input.Password placeholder="sk_test_..." />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={['integrations', 'stripe', 'publishableKey']}
            label="Publishable Key"
            rules={[{ pattern: /^pk_/, message: 'Publishable key must start with pk_' }]}
          >
            <Input placeholder="pk_test_..." />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name={['integrations', 'stripe', 'webhookSecret']}
            label="Webhook Secret"
          >
            <Input.Password placeholder="whsec_..." />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={['integrations', 'stripe', 'currency']}
            label="Currency"
            initialValue="usd"
          >
            <Select>
              <Option value="usd">USD</Option>
              <Option value="eur">EUR</Option>
              <Option value="gbp">GBP</Option>
              <Option value="cad">CAD</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name={['integrations', 'stripe', 'companyName']}
            label="Company Name (for receipts)"
          >
            <Input placeholder="Company name for Stripe receipts" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={['integrations', 'stripe', 'terminalEnabled']}
            label="Terminal Enabled"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name={['integrations', 'stripe', 'terminalLocationId']}
        label="Terminal Location ID"
        extra="Usually auto-provisioned from the business app Card Readers tab (Connect). Override only if needed."
      >
        <Input placeholder="tml_..." />
      </Form.Item>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 0 }}
        message="Physical readers"
        description="Businesses buy Stripe Terminal hardware in-app (Payment Settings → Card Readers). Stripe ships and bills the connected account at list price. Readers registered to this location appear in the business Card Readers tab."
      />
    </Card>
  )

  const helcimConfigTab = (
    <Card title="Helcim Configuration" style={{ marginBottom: '16px' }}>
      <Alert
        message="Helcim tenant card processor"
        description="Configure Helcim for this business. When connected via the main app, Helcim becomes the active card processor and Stripe is disabled for new checkouts."
        type="info"
        style={{ marginBottom: '16px' }}
      />
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name={['integrations', 'preferredCardProcessor']} label="Preferred card processor">
            <Select allowClear placeholder="Default (stripe)">
              <Option value="stripe">Stripe</Option>
              <Option value="helcim">Helcim</Option>
              <Option value="finix">Finix</Option>
              <Option value="banccard">BancCard</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={['integrations', 'helcim', 'environment']} label="Environment">
            <Select>
              <Option value="live">Live</Option>
              <Option value="test">Test</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name={['integrations', 'helcim', 'apiToken']} label="API Token">
            <Input.Password placeholder="Helcim API token" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={['integrations', 'helcim', 'webhookVerifierToken']} label="Webhook verifier token">
            <Input.Password placeholder="From Helcim webhook settings" />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name={['integrations', 'helcim', 'currency']} label="Currency">
            <Select>
              <Option value="USD">USD</Option>
              <Option value="CAD">CAD</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={['integrations', 'helcim', 'terminalId']} label="Terminal ID">
            <Input placeholder="Optional terminal hardware ID" />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name={['integrations', 'helcim', 'isEnabled']} label="Helcim enabled" valuePropName="checked">
        <Switch />
      </Form.Item>
    </Card>
  )

  // Helper function to check if an integration is configured
  const isIntegrationConfigured = (integration: any): boolean => {
    if (!integration) return false
    
    // Check different integration types
    if (integration.apiKey || integration.accountSid || integration.clientId) {
      return true
    }
    
    return false
  }

  // Get integration status for display
  const getIntegrationStatus = () => {
    const integrations = settings.integrations || {}
    const status = {
      telnyx: isIntegrationConfigured(integrations.telnyx),
      twilio: isIntegrationConfigured(integrations.twilio),
      mailgun: isIntegrationConfigured(integrations.mailgun),
      plaid: isIntegrationConfigured(integrations.plaid),
      openai: isIntegrationConfigured(integrations.openai) && integrations.openai?.isEnabled
    }
    return status
  }

  const integrationStatus = getIntegrationStatus()

  const integrationsTab = (
    <div>
      <Card title="Integration Status" style={{ marginBottom: '16px' }}>
        <Alert
          message="Configured Services"
          description={
            <div>
              <Space wrap>
                {integrationStatus.telnyx && <Tag color="green">Telnyx ✓</Tag>}
                {integrationStatus.twilio && <Tag color="blue">Twilio ✓</Tag>}
                {integrationStatus.mailgun && <Tag color="orange">Mailgun ✓</Tag>}
                {integrationStatus.plaid && <Tag color="purple">Plaid ✓</Tag>}
                {integrationStatus.openai && <Tag color="cyan">OpenAI ✓</Tag>}
                {!integrationStatus.telnyx && !integrationStatus.twilio && !integrationStatus.mailgun && 
                 !integrationStatus.plaid && !integrationStatus.openai && (
                  <Tag color="default">No integrations configured</Tag>
                )}
              </Space>
            </div>
          }
          type="info"
          style={{ marginBottom: '16px' }}
        />
      </Card>

      <Card title="SMS Provider Preference" style={{ marginBottom: '16px' }}>
        <Alert
          message="SMS Provider Configuration"
          description="Choose your preferred SMS provider. The system will use this provider first, then fallback to the other if it fails."
          type="info"
          style={{ marginBottom: '16px' }}
        />
        <Form.Item
          name={['integrations', 'preferredSmsProvider']}
          label="Preferred SMS Provider"
          initialValue="auto"
        >
          <Select>
            <Option value="auto">Auto (Telnyx → Twilio)</Option>
            <Option value="telnyx">Telnyx First</Option>
            <Option value="twilio">Twilio First</Option>
          </Select>
        </Form.Item>
      </Card>

      <Card title="Telnyx (SMS/Voice)" style={{ marginBottom: '16px' }}>
        <Alert
          message="Telnyx Integration"
          description="Configure Telnyx for SMS and voice capabilities with global coverage and low latency."
          type="info"
          style={{ marginBottom: '16px' }}
        />
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name={['integrations', 'telnyx', 'apiKey']}
              label="API Key"
            >
              <Input.Password placeholder="KEY..." />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={['integrations', 'telnyx', 'phoneNumber']}
              label="Phone Number"
            >
              <Input placeholder="+1234567890" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={['integrations', 'telnyx', 'messagingProfileId']}
              label="Messaging Profile ID (Optional)"
            >
              <Input placeholder="Profile ID" />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card title="Twilio (SMS/Voice)" style={{ marginBottom: '16px' }}>
        <Alert
          message="Twilio Integration"
          description="Configure Twilio for SMS and voice capabilities. Works as a fallback if Telnyx is configured."
          type="info"
          style={{ marginBottom: '16px' }}
        />
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name={['integrations', 'twilio', 'accountSid']}
              label="Account SID"
            >
              <Input placeholder="AC..." />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={['integrations', 'twilio', 'authToken']}
              label="Auth Token"
            >
              <Input.Password placeholder="Auth token" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={['integrations', 'twilio', 'phoneNumber']}
              label="Phone Number"
            >
              <Input placeholder="+1234567890" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name={['integrations', 'twilio', 'testPhoneNumber']}
              label="Test Phone Number (Optional)"
            >
              <Input placeholder="+1234567890" />
            </Form.Item>
          </Col>
        </Row>
        <Divider orientation="left">Browser Calling Configuration (Optional)</Divider>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name={['integrations', 'twilio', 'apiKey']}
              label="API Key (for Browser Calling)"
            >
              <Input placeholder="SK..." />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={['integrations', 'twilio', 'apiSecret']}
              label="API Secret (for Browser Calling)"
            >
              <Input.Password placeholder="API Secret" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={['integrations', 'twilio', 'twimlAppSid']}
              label="TwiML App SID (for Browser Calling)"
            >
              <Input placeholder="AP..." />
            </Form.Item>
          </Col>
        </Row>
        <Divider orientation="left">Contact Center Configuration (Optional)</Divider>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name={['integrations', 'twilio', 'taskRouterWorkspaceSid']}
              label="TaskRouter Workspace SID"
            >
              <Input placeholder="WS..." />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={['integrations', 'twilio', 'chatServiceSid']}
              label="Chat Service SID"
            >
              <Input placeholder="IS..." />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card title="Mailgun (Email)" style={{ marginBottom: '16px' }}>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name={['integrations', 'mailgun', 'apiKey']}
              label="API Key"
            >
              <Input.Password placeholder="key-..." />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={['integrations', 'mailgun', 'domain']}
              label="Domain"
            >
              <Input placeholder="mg.example.com" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={['integrations', 'mailgun', 'fromEmail']}
              label="From Email"
            >
              <Input placeholder="noreply@example.com" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name={['integrations', 'mailgun', 'region']}
              label="Region"
              initialValue="us"
            >
              <Select>
                <Option value="us">US</Option>
                <Option value="eu">EU</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card title="Plaid (Banking)" style={{ marginBottom: '16px' }}>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name={['integrations', 'plaid', 'clientId']}
              label="Client ID"
            >
              <Input placeholder="Plaid client ID" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={['integrations', 'plaid', 'secret']}
              label="Secret"
            >
              <Input.Password placeholder="Plaid secret" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={['integrations', 'plaid', 'environment']}
              label="Environment"
              initialValue="sandbox"
            >
              <Select>
                <Option value="sandbox">Sandbox</Option>
                <Option value="development">Development</Option>
                <Option value="production">Production</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card title="OpenAI (AI & Automation)" style={{ marginBottom: '16px' }}>
        <Alert
          message="OpenAI Integration"
          description="Configure OpenAI for AI-powered features including call monitoring, email assistance, and analytics. Each business uses their own API key."
          type="info"
          style={{ marginBottom: '16px' }}
        />
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name={['integrations', 'openai', 'apiKey']}
              label="API Key"
              rules={[{ required: false, message: 'API key is required to enable OpenAI features' }]}
            >
              <Input.Password placeholder="sk-..." />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={['integrations', 'openai', 'organization']}
              label="Organization ID (Optional)"
            >
              <Input placeholder="org-..." />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name={['integrations', 'openai', 'model']}
              label="Default Model"
              initialValue="gpt-3.5-turbo"
            >
              <Select>
                <Option value="gpt-4">GPT-4</Option>
                <Option value="gpt-4-turbo">GPT-4 Turbo</Option>
                <Option value="gpt-4o">GPT-4o (higher token limit)</Option>
                <Option value="gpt-4o-mini">GPT-4o Mini</Option>
                <Option value="gpt-3.5-turbo">GPT-3.5 Turbo</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={['integrations', 'openai', 'maxTokens']}
              label="Max Tokens (Optional)"
            >
              <InputNumber placeholder="4000" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Divider orientation="left">Feature Toggles</Divider>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name={['integrations', 'openai', 'isEnabled']}
              valuePropName="checked"
              label="Enable OpenAI Integration"
            >
              <Switch />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name={['integrations', 'openai', 'features', 'callMonitoring']}
              valuePropName="checked"
              label="AI Call Monitoring"
            >
              <Switch />
            </Form.Item>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Real-time transcription, suggestions, and form auto-fill during calls
            </Text>
          </Col>
          <Col span={8}>
            <Form.Item
              name={['integrations', 'openai', 'features', 'emailAssistant']}
              valuePropName="checked"
              label="Email Assistant"
            >
              <Switch />
            </Form.Item>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              AI-powered email generation and responses
            </Text>
          </Col>
          <Col span={8}>
            <Form.Item
              name={['integrations', 'openai', 'features', 'analytics']}
              valuePropName="checked"
              label="AI Analytics"
            >
              <Switch />
            </Form.Item>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Business intelligence and predictive analytics
            </Text>
          </Col>
        </Row>
      </Card>
    </div>
  )

  const notificationsTab = (
    <>
      <Card title="Notification Preferences">
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name={['notifications', 'email']}
              label="Email Notifications"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={['notifications', 'sms']}
              label="SMS Notifications"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={['notifications', 'push']}
              label="Push Notifications"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card title="Invoice Profit Digest" style={{ marginTop: 16 }}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Manual send"
          description="Queues an invoice profit digest immediately for this business. Recipients must be configured in notification settings. Manual sends bypass schedule and deduplication. Optional custom start/end dates override the default window and bypass the matching report toggle."
        />
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space wrap>
            <Select
              value={digestFrequency}
              onChange={setDigestFrequency}
              style={{ width: 160 }}
              options={[
                { value: 'daily', label: 'Daily digest' },
                { value: 'weekly', label: 'Weekly digest' },
                { value: 'monthly', label: 'Monthly digest' },
              ]}
            />
            <Button
              type="primary"
              loading={triggeringDigest}
              onClick={async () => {
                if (!id) return
                if (digestUseCustomPeriod) {
                  const [start, end] = digestCustomPeriod ?? []
                  if (!start?.isValid() || !end?.isValid()) {
                    message.error('Select a custom start and end date')
                    return
                  }
                }
                setTriggeringDigest(true)
                try {
                  const payload: {
                    frequency: typeof digestFrequency
                    startDate?: string
                    endDate?: string
                  } = { frequency: digestFrequency }
                  if (digestUseCustomPeriod && digestCustomPeriod?.[0] && digestCustomPeriod?.[1]) {
                    payload.startDate = digestCustomPeriod[0].format('YYYY-MM-DD')
                    payload.endDate = digestCustomPeriod[1].format('YYYY-MM-DD')
                  }
                  const res = await api.post(`/businesses/${id}/invoice-profit-digest/send`, payload)
                  message.success(res.data.message || 'Digest queued')
                } catch (e: any) {
                  message.error(e.response?.data?.message || 'Failed to trigger digest')
                } finally {
                  setTriggeringDigest(false)
                }
              }}
            >
              Send digest now
            </Button>
          </Space>
          <Checkbox
            checked={digestUseCustomPeriod}
            onChange={(e) => setDigestUseCustomPeriod(e.target.checked)}
          >
            Use custom report period
          </Checkbox>
          {digestUseCustomPeriod ? (
            <DatePicker.RangePicker
              value={digestCustomPeriod}
              onChange={(values) => setDigestCustomPeriod(values)}
              allowClear
              style={{ width: '100%', maxWidth: 320 }}
            />
          ) : null}
        </Space>
      </Card>
    </>
  )

  const brandingTab = (
    <Card title="Branding & Appearance">
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name={['branding', 'primaryColor']}
            label="Primary Color"
          >
            <Input type="color" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={['branding', 'secondaryColor']}
            label="Secondary Color"
          >
            <Input type="color" />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item
        name={['branding', 'logo']}
        label="Logo URL"
      >
        <Input placeholder="https://example.com/logo.png" />
      </Form.Item>
    </Card>
  )

  const featuresTab = (
    <Card title="Feature Toggles">
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name={['features', 'aiAssistant']}
            label="AI Assistant"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={['features', 'advancedReporting']}
            label="Advanced Reporting"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name={['features', 'inventoryManagement']}
            label="Inventory Management"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={['features', 'timeTracking']}
            label="Time Tracking"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Col>
      </Row>
    </Card>
  )

  // Format JSON helper
  const formatJson = () => {
    try {
      const parsed = JSON.parse(rawJson)
      const formatted = JSON.stringify(parsed, null, 2)
      setRawJson(formatted)
      setJsonError(null)
      message.success('JSON formatted successfully')
    } catch (error: any) {
      setJsonError(error.message)
      message.error('Invalid JSON: ' + error.message)
    }
  }

  // Validate JSON helper
  const validateJson = (jsonString: string): { valid: boolean; error?: string; data?: any } => {
    try {
      const parsed = JSON.parse(jsonString)
      return { valid: true, data: parsed }
    } catch (error: any) {
      return { valid: false, error: error.message }
    }
  }

  // Handle raw JSON change
  const handleRawJsonChange = (value: string) => {
    setRawJson(value)
    const validation = validateJson(value)
    if (validation.valid) {
      setJsonError(null)
    } else {
      setJsonError(validation.error || 'Invalid JSON')
    }
  }

  const rawJsonTab = (
    <Card title="Raw JSON Editor">
      <Alert
        message="Warning: Advanced Editing"
        description="Editing raw JSON directly can break settings if invalid. Always validate JSON before saving. Changes here will replace all settings."
        type="warning"
        showIcon
        style={{ marginBottom: '16px' }}
      />
      
      <div style={{ marginBottom: '16px' }}>
        <Space>
          <Button onClick={formatJson}>Format JSON</Button>
          <Switch
            checked={useRawJson}
            onChange={setUseRawJson}
            checkedChildren="Raw JSON Mode"
            unCheckedChildren="Form Mode"
          />
          {jsonError && (
            <Text type="danger">JSON Error: {jsonError}</Text>
          )}
        </Space>
      </div>

      <TextArea
        value={rawJson}
        onChange={(e) => handleRawJsonChange(e.target.value)}
        rows={20}
        style={{ 
          fontFamily: 'monospace',
          fontSize: '12px',
          borderColor: jsonError ? '#ff4d4f' : undefined
        }}
        placeholder='{"integrations": {"stripe": {...}}, ...}'
      />

      <Alert
        message="Instructions"
        description={
          <ul style={{ margin: 0, paddingLeft: '20px' }}>
            <li>Edit the JSON directly to modify any setting</li>
            <li>Click "Format JSON" to pretty-print</li>
            <li>Enable "Raw JSON Mode" to save raw JSON instead of form values</li>
            <li>Invalid JSON will prevent saving</li>
            <li>This will replace all settings with the JSON structure provided</li>
          </ul>
        }
        type="info"
        style={{ marginTop: '16px' }}
      />
    </Card>
  )

  interface PaidAddonRow {
    name: string
    label: string
    monthlyFeeCents: number
    isEnabled: boolean
    billingWaived: boolean
    activatedAt: string | null
    waivedAt: string | null
  }

  const PaidAddonsTab: React.FC = () => {
    const [addons, setAddons] = useState<PaidAddonRow[]>([])
    const [loading, setLoading] = useState(true)
    const [actionKey, setActionKey] = useState<string | null>(null)
    const [wlCodeAccess, setWlCodeAccess] = useState<{
      entitled: boolean
      hasCodeAccess: boolean
    } | null>(null)

    const fetchAddons = async () => {
      if (!id) return
      setLoading(true)
      try {
        const res = await api.get(`/businesses/${id}/paid-addons`)
        setAddons(res.data?.addons || [])
        try {
          const codeRes = await api.get(`/businesses/${id}/white-label-code-access`)
          setWlCodeAccess({
            entitled: Boolean(codeRes.data?.entitled),
            hasCodeAccess: Boolean(codeRes.data?.hasCodeAccess),
          })
        } catch {
          setWlCodeAccess(null)
        }
      } catch (error: any) {
        console.error('Failed to fetch paid add-ons:', error)
        message.error(error.response?.data?.message || 'Failed to load paid add-ons')
      } finally {
        setLoading(false)
      }
    }

    useEffect(() => {
      fetchAddons()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id])

    const grantWlCodeAccess = () => {
      Modal.confirm({
        title: 'Grant Enterprise code access?',
        content:
          'Unlocks GitHub collaborator invites and ServiceFlow Pro IDE for this business after the $1,500,000 white-label ownership deal is signed. Does not charge Stripe.',
        okText: 'Grant code access',
        onOk: async () => {
          setActionKey('wl-code:grant')
          try {
            const res = await api.post(`/businesses/${id}/white-label-code-access/grant`)
            message.success(res.data?.message || 'Enterprise code access granted')
            await fetchAddons()
          } catch (error: any) {
            message.error(error.response?.data?.message || 'Failed to grant code access')
          } finally {
            setActionKey(null)
          }
        },
      })
    }

    const revokeWlCodeAccess = () => {
      Modal.confirm({
        title: 'Revoke Enterprise code access?',
        content: 'Hides repo URLs in ServiceFlow UI and blocks IDE clone / GitHub invite APIs.',
        okText: 'Revoke',
        okButtonProps: { danger: true },
        onOk: async () => {
          setActionKey('wl-code:revoke')
          try {
            const res = await api.post(`/businesses/${id}/white-label-code-access/revoke`)
            message.success(res.data?.message || 'Enterprise code access revoked')
            await fetchAddons()
          } catch (error: any) {
            message.error(error.response?.data?.message || 'Failed to revoke code access')
          } finally {
            setActionKey(null)
          }
        },
      })
    }

    const grantAddon = (addon: PaidAddonRow) => {
      Modal.confirm({
        title: `Enable ${addon.label} for free?`,
        content:
          'This grants access without charging the customer and removes any Stripe add-on line item from their subscription.',
        okText: 'Enable free',
        onOk: async () => {
          setActionKey(`${addon.name}:grant`)
          try {
            const res = await api.post(`/businesses/${id}/paid-addons/${addon.name}/grant`)
            message.success(res.data?.message || `${addon.label} enabled for free`)
            await fetchAddons()
          } catch (error: any) {
            message.error(error.response?.data?.message || `Failed to enable ${addon.label}`)
          } finally {
            setActionKey(null)
          }
        },
      })
    }

    const revokeAddon = (addon: PaidAddonRow) => {
      Modal.confirm({
        title: `Disable ${addon.label}?`,
        content:
          'This revokes entitlement and removes any Stripe add-on line item from their subscription.',
        okText: 'Disable',
        okButtonProps: { danger: true },
        onOk: async () => {
          setActionKey(`${addon.name}:revoke`)
          try {
            const res = await api.post(`/businesses/${id}/paid-addons/${addon.name}/revoke`)
            message.success(res.data?.message || `${addon.label} disabled`)
            await fetchAddons()
          } catch (error: any) {
            message.error(error.response?.data?.message || `Failed to disable ${addon.label}`)
          } finally {
            setActionKey(null)
          }
        },
      })
    }

    return (
      <Card title="Paid Add-ons (platform grant)">
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Enable add-ons without charging the business"
          description="Granting an add-on entitles the business and strips any Stripe add-on charge. Disabling revokes access and also strips the Stripe charge."
        />
        <Card
          type="inner"
          title="White-label Enterprise code access ($1.5M deal)"
          style={{ marginBottom: 16 }}
          extra={
            wlCodeAccess?.hasCodeAccess ? (
              <Tag color="green">Unlocked</Tag>
            ) : (
              <Tag>Locked</Tag>
            )
          }
        >
          <Paragraph type="secondary" style={{ marginBottom: 12 }}>
            Light ($1,500/mo) can activate Studio + domain. Grant code access only after the signed
            $1,500,000 ownership agreement — unlocks GitHub invites and IDE clone.
          </Paragraph>
          <Space>
            <Button
              type="primary"
              loading={actionKey === 'wl-code:grant'}
              disabled={Boolean(wlCodeAccess?.hasCodeAccess)}
              onClick={grantWlCodeAccess}
            >
              Grant code access
            </Button>
            <Button
              danger
              loading={actionKey === 'wl-code:revoke'}
              disabled={!wlCodeAccess?.hasCodeAccess}
              onClick={revokeWlCodeAccess}
            >
              Revoke code access
            </Button>
          </Space>
        </Card>
        <Table
          rowKey="name"
          loading={loading}
          dataSource={addons}
          pagination={false}
          columns={[
            { title: 'Add-on', dataIndex: 'label', key: 'label' },
            {
              title: 'List price',
              key: 'price',
              render: (_: unknown, row: PaidAddonRow) =>
                `$${(row.monthlyFeeCents / 100).toFixed(2)}/mo`,
            },
            {
              title: 'Enabled',
              key: 'isEnabled',
              render: (_: unknown, row: PaidAddonRow) =>
                row.isEnabled ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>,
            },
            {
              title: 'Billing waived',
              key: 'billingWaived',
              render: (_: unknown, row: PaidAddonRow) =>
                row.billingWaived ? <Tag color="blue">Comped</Tag> : <Tag>Paid / none</Tag>,
            },
            {
              title: 'Activated',
              key: 'activatedAt',
              render: (_: unknown, row: PaidAddonRow) =>
                row.activatedAt ? dayjs(row.activatedAt).format('MMM D, YYYY h:mm A') : '—',
            },
            {
              title: 'Actions',
              key: 'actions',
              render: (_: unknown, row: PaidAddonRow) => (
                <Space>
                  <Button
                    type="primary"
                    size="small"
                    loading={actionKey === `${row.name}:grant`}
                    disabled={row.isEnabled && row.billingWaived}
                    onClick={() => grantAddon(row)}
                  >
                    Enable free
                  </Button>
                  <Button
                    danger
                    size="small"
                    loading={actionKey === `${row.name}:revoke`}
                    disabled={!row.isEnabled && !row.billingWaived}
                    onClick={() => revokeAddon(row)}
                  >
                    Disable
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      </Card>
    )
  }

  const SubscriptionTab: React.FC = () => {
    const [subData, setSubData] = useState<any>(null)
    const [subLoading, setSubLoading] = useState(true)
    const [subSaving, setSubSaving] = useState(false)
    const [subForm] = Form.useForm()
    const [offerForm] = Form.useForm()
    const [offers, setOffers] = useState<any[]>([])
    const [offersLoading, setOffersLoading] = useState(false)
    const [sendingOffer, setSendingOffer] = useState(false)
    const [offerSendResult, setOfferSendResult] = useState<any>(null)
    const [offersLoadError, setOffersLoadError] = useState<string | null>(null)
    const [catalogPlans, setCatalogPlans] = useState<any[]>([])
    const [copyFromPlanId, setCopyFromPlanId] = useState<string | undefined>(undefined)
    const [copiedFeatures, setCopiedFeatures] = useState<string[]>([])

    const fetchSubscription = async () => {
      setSubLoading(true)
      try {
        const res = await api.get(`/businesses/${id}/subscription`)
        setSubData(res.data)
        subForm.setFieldsValue({
          subscriptionPlan: res.data.business?.subscriptionPlan || 'starter',
          subscriptionStatus: res.data.business?.subscriptionStatus || 'active',
          subscriptionEndsAt: res.data.business?.subscriptionEndsAt ? dayjs(res.data.business.subscriptionEndsAt) : null,
          trialEndDate: res.data.subscription?.trialEndDate ? dayjs(res.data.subscription.trialEndDate) : null,
          isActive: res.data.business?.isActive ?? true,
          stripeSubscriptionId: res.data.subscription?.stripeSubscriptionId || '',
          stripeCustomerId: res.data.subscription?.stripeCustomerId || '',
        })
      } catch (error) {
        console.error('Failed to fetch subscription:', error)
        message.error('Failed to load subscription data')
      } finally {
        setSubLoading(false)
      }
    }

    const fetchOffers = async () => {
      setOffersLoading(true)
      setOffersLoadError(null)
      try {
        const res = await api.get(`/businesses/${id}/subscription-offers`)
        setOffers(res.data?.data || [])
      } catch (error: any) {
        console.error('Failed to fetch subscription offers:', error)
        const msg =
          error.response?.data?.message ||
          error.message ||
          'Failed to load subscription offers'
        setOffersLoadError(msg)
        message.error(msg)
      } finally {
        setOffersLoading(false)
      }
    }

    const fetchCatalogPlans = async () => {
      try {
        const res = await api.get('/subscription-plans?status=active&limit=100')
        const plans = Array.isArray(res.data?.plans) ? res.data.plans : []
        setCatalogPlans(
          [...plans].sort((a, b) => Number(a.basePrice || 0) - Number(b.basePrice || 0))
        )
      } catch (error) {
        console.error('Failed to fetch catalog plans for offer copy:', error)
      }
    }

    useEffect(() => {
      fetchSubscription()
      fetchOffers()
      fetchCatalogPlans()
    }, [id])

    const formatPlanFeatureLabel = (feature: string): string => {
      const trimmed = feature.trim()
      if (!trimmed) return ''
      if (/\s/.test(trimmed) || /[A-Z]/.test(trimmed)) return trimmed
      return trimmed
        .split(/[_-]+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')
    }

    const buildOfferMessageFromPlan = (plan: any): string => {
      const lines: string[] = [
        `Here's what's included with ${plan.name}:`,
      ]

      const featureList = Array.isArray(plan.features)
        ? plan.features.filter((f: unknown): f is string => typeof f === 'string' && f.trim().length > 0)
        : []

      if (featureList.length > 0) {
        for (const feature of featureList) {
          const label = formatPlanFeatureLabel(feature)
          if (label) lines.push(`• ${label}`)
        }
      }

      lines.push(`• Up to ${Number(plan.userLimit || 5)} users`)
      if (plan.includesIntegrations) lines.push('• Integrations')
      if (plan.includesMobile) lines.push('• Mobile app')
      if (plan.includesAICallAgent) lines.push('• AI call agent')
      if (plan.smsLimitPerMonth != null) lines.push(`• SMS: ${plan.smsLimitPerMonth}/month`)
      if (plan.emailLimitPerMonth != null) lines.push(`• Email: ${plan.emailLimitPerMonth}/month`)
      if (plan.callMinutesPerMonth != null) {
        lines.push(`• Call minutes: ${plan.callMinutesPerMonth}/month`)
      }
      const trialDays = Number(plan.trialDays || 0)
      if (trialDays > 0) lines.push(`• ${trialDays}-day free trial`)

      return lines.join('\n')
    }

    const handleCopyFromPlan = (planId: string | undefined) => {
      setCopyFromPlanId(planId)
      if (!planId) {
        setCopiedFeatures([])
        return
      }
      const plan = catalogPlans.find((p) => p.id === planId)
      if (!plan) return

      const trialDays = Number(plan.trialDays || 0)
      const featureList = Array.isArray(plan.features)
        ? plan.features.filter((f: unknown): f is string => typeof f === 'string' && f.trim().length > 0)
        : []
      setCopiedFeatures(featureList)

      offerForm.setFieldsValue({
        name: plan.name,
        monthlyAmount: Number(plan.basePrice || 0),
        userLimit: Number(plan.userLimit || 5),
        includesIntegrations: Boolean(plan.includesIntegrations),
        includesMobile: Boolean(plan.includesMobile),
        includesAICallAgent: Boolean(plan.includesAICallAgent),
        smsLimitPerMonth: plan.smsLimitPerMonth ?? undefined,
        emailLimitPerMonth: plan.emailLimitPerMonth ?? undefined,
        callMinutesPerMonth: plan.callMinutesPerMonth ?? undefined,
        description: plan.description || undefined,
        adminMessage: buildOfferMessageFromPlan(plan),
        includeTrial: trialDays > 0,
        trialDays: trialDays > 0 ? trialDays : 14,
      })
      message.success(`Copied from ${plan.name} — adjust any fields before sending`)
    }
    const handleSendOffer = async (values: any) => {
      setSendingOffer(true)
      setOfferSendResult(null)
      try {
        const payload = {
          ...values,
          trialDays: values.includeTrial ? Number(values.trialDays || 0) : 0,
          features: copiedFeatures.length > 0 ? copiedFeatures : values.features,
        }
        delete payload.includeTrial
        const res = await api.post(`/businesses/${id}/subscription-offers`, payload)
        const offer = res.data?.data
        setOfferSendResult(offer)
        offerForm.resetFields()
        setCopyFromPlanId(undefined)
        setCopiedFeatures([])
        await fetchOffers()
        if (offer?.emailWarning) {
          message.warning(offer.emailWarning)
        } else {
          message.success(
            offer?.emailsSent
              ? `Custom subscription offer sent to ${offer.emailsSent} recipient(s)`
              : 'Custom subscription offer created'
          )
        }
      } catch (error: any) {
        const msg =
          error.response?.data?.message ||
          error.message ||
          'Failed to send subscription offer'
        setOfferSendResult({ error: msg })
        message.error(msg)
      } finally {
        setSendingOffer(false)
      }
    }

    const handleOfferValidationFailed = () => {
      message.error('Please fill in plan name, monthly price, and user limit before sending.')
    }

    const handleCancelOffer = async (offerId: string) => {
      try {
        await api.post(`/businesses/${id}/subscription-offers/${offerId}/cancel`)
        message.success('Offer cancelled')
        await fetchOffers()
      } catch (error: any) {
        message.error(error.response?.data?.message || 'Failed to cancel offer')
      }
    }

    const handleResendOffer = async (offerId: string) => {
      try {
        await api.post(`/businesses/${id}/subscription-offers/${offerId}/resend`)
        message.success('Offer email resent')
        await fetchOffers()
      } catch (error: any) {
        message.error(error.response?.data?.message || 'Failed to resend offer')
      }
    }

    const copyCheckoutLink = async (url?: string | null) => {
      if (!url) {
        message.warning('No checkout link available')
        return
      }
      try {
        await navigator.clipboard.writeText(url)
        message.success('Checkout link copied')
      } catch {
        message.error('Could not copy link')
      }
    }

    const handleSubSave = async (values: any) => {
      setSubSaving(true)
      try {
        const res = await api.put(`/businesses/${id}/subscription`, {
          subscriptionPlan: values.subscriptionPlan,
          subscriptionStatus: values.subscriptionStatus,
          subscriptionEndsAt: values.subscriptionEndsAt?.toISOString() || null,
          trialEndDate: values.trialEndDate?.toISOString() || null,
          isActive: values.isActive,
          stripeSubscriptionId: values.stripeSubscriptionId?.trim() || null,
          stripeCustomerId: values.stripeCustomerId?.trim() || null,
        })
        message.success(res.data?.message || 'Subscription updated successfully')
        await fetchSubscription()
      } catch (error: any) {
        console.error('Failed to save subscription:', error)
        message.error(error.response?.data?.message || 'Failed to update subscription')
      } finally {
        setSubSaving(false)
      }
    }

    const handleStripeSave = async () => {
      setSubSaving(true)
      try {
        const values = await subForm.validateFields(['stripeSubscriptionId', 'stripeCustomerId'])
        const res = await api.put(`/businesses/${id}/subscription`, {
          stripeSubscriptionId: values.stripeSubscriptionId?.trim() || null,
          stripeCustomerId: values.stripeCustomerId?.trim() || null,
        })
        message.success(res.data?.message || 'Stripe billing link updated')
        await fetchSubscription()
      } catch (error: any) {
        if (error?.errorFields) return
        console.error('Failed to save Stripe IDs:', error)
        message.error(error.response?.data?.message || 'Failed to update Stripe billing link')
      } finally {
        setSubSaving(false)
      }
    }

    const handleStripeSync = async () => {
      setSubSaving(true)
      try {
        const values = subForm.getFieldsValue(['stripeSubscriptionId', 'stripeCustomerId'])
        const res = await api.put(`/businesses/${id}/subscription`, {
          stripeSubscriptionId: values.stripeSubscriptionId?.trim() || null,
          stripeCustomerId: values.stripeCustomerId?.trim() || null,
          syncFromStripe: true,
        })
        message.success(
          res.data?.message ||
            'Synced from Stripe. Status and billing dates were updated from the live subscription.'
        )
        await fetchSubscription()
      } catch (error: any) {
        console.error('Failed to sync from Stripe:', error)
        message.error(error.response?.data?.message || 'Failed to sync from Stripe')
      } finally {
        setSubSaving(false)
      }
    }

    const grantFullAccess = async () => {
      setSubSaving(true)
      try {
        const res = await api.put(`/businesses/${id}/subscription`, {
          subscriptionStatus: 'active',
          isActive: true,
        })
        message.success(
          res.data?.message ||
            'Full access granted. The business owner should log out and back in if they still see the upgrade screen.'
        )
        await fetchSubscription()
      } catch (error: any) {
        message.error(error.response?.data?.message || 'Failed to grant full access')
      } finally {
        setSubSaving(false)
      }
    }

    const startTrial = async (days: number) => {
      setSubSaving(true)
      try {
        await api.put(`/businesses/${id}/subscription`, {
          subscriptionStatus: 'trial',
          trialDays: days,
        })
        message.success(`${days}-day trial started`)
        await fetchSubscription()
      } catch (error: any) {
        message.error(error.response?.data?.message || 'Failed to start trial')
      } finally {
        setSubSaving(false)
      }
    }

    if (subLoading) return <Spin tip="Loading subscription data..." />

    const trialEnd = subData?.subscription?.trialEndDate ? dayjs(subData.subscription.trialEndDate) : null
    const isOnTrial = subData?.business?.subscriptionStatus === 'trial' && trialEnd && trialEnd.isAfter(dayjs())
    const trialDaysLeft = isOnTrial ? trialEnd.diff(dayjs(), 'day') : 0

    return (
      <div>
        <Card title="Current Status" style={{ marginBottom: 16 }}>
          <Row gutter={24}>
            <Col span={6}>
              <Statistic title="Plan" value={subData?.business?.subscriptionPlan || 'N/A'} />
            </Col>
            <Col span={6}>
              <Statistic
                title="Status"
                value={subData?.business?.subscriptionStatus || 'N/A'}
                valueStyle={{ color: subData?.business?.subscriptionStatus === 'active' ? '#3f8600' : subData?.business?.subscriptionStatus === 'trial' ? '#1890ff' : '#cf1322' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Trial Days Left"
                value={isOnTrial ? trialDaysLeft : 'N/A'}
                prefix={isOnTrial ? <ClockCircleOutlined /> : undefined}
                valueStyle={{ color: isOnTrial ? (trialDaysLeft <= 3 ? '#cf1322' : '#1890ff') : undefined }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Active"
                value={subData?.business?.isActive ? 'Yes' : 'No'}
                valueStyle={{ color: subData?.business?.isActive ? '#3f8600' : '#cf1322' }}
              />
            </Col>
          </Row>
          {subData?.subscription?.trialEndDate && (
            <Descriptions size="small" style={{ marginTop: 16 }} column={2}>
              <Descriptions.Item label="Trial End Date">
                {dayjs(subData.subscription.trialEndDate).format('MMM D, YYYY h:mm A')}
              </Descriptions.Item>
              {subData?.business?.subscriptionEndsAt && (
                <Descriptions.Item label="Subscription Ends">
                  {dayjs(subData.business.subscriptionEndsAt).format('MMM D, YYYY h:mm A')}
                </Descriptions.Item>
              )}
            </Descriptions>
          )}
          {(subData?.subscription?.stripeSubscriptionId || subData?.subscription?.stripeCustomerId) && (
            <Descriptions size="small" style={{ marginTop: 16 }} column={1}>
              {subData?.subscription?.stripeSubscriptionId && (
                <Descriptions.Item label="Stripe Subscription">
                  <Text copyable code>{subData.subscription.stripeSubscriptionId}</Text>
                </Descriptions.Item>
              )}
              {subData?.subscription?.stripeCustomerId && (
                <Descriptions.Item label="Stripe Customer">
                  <Text copyable code>{subData.subscription.stripeCustomerId}</Text>
                </Descriptions.Item>
              )}
              {subData?.subscription?.currentPeriodEnd && (
                <Descriptions.Item label="Current Period Ends">
                  {dayjs(subData.subscription.currentPeriodEnd).format('MMM D, YYYY h:mm A')}
                </Descriptions.Item>
              )}
            </Descriptions>
          )}
        </Card>

        <Card title="Quick Actions" style={{ marginBottom: 16 }}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Paywall bypass"
            description="Use Grant Full Access (sets Active + verifies owner) or extend Trial End Date and Save. The business user must log out and back in if they still see the upgrade screen."
          />
          <Space wrap>
            <Button type="primary" onClick={grantFullAccess} loading={subSaving}>
              Grant Full Access
            </Button>
            <Button onClick={() => startTrial(7)} loading={subSaving}>Start 7-Day Trial</Button>
            <Button onClick={() => startTrial(14)} loading={subSaving}>Start 14-Day Trial</Button>
            <Button onClick={() => startTrial(30)} loading={subSaving}>Start 30-Day Trial</Button>
            <Button
              danger
              onClick={async () => {
                setSubSaving(true)
                try {
                  await api.put(`/businesses/${id}/subscription`, { subscriptionStatus: 'expired', trialEndDate: null })
                  message.success('Trial expired')
                  await fetchSubscription()
                } catch (e: any) { message.error(e.response?.data?.message || 'Failed') }
                finally { setSubSaving(false) }
              }}
              loading={subSaving}
            >
              Expire Trial
            </Button>
            <Button
              onClick={async () => {
                setTriggeringLateFees(true)
                try {
                  const res = await api.post(`/businesses/${id}/trigger-late-fees`)
                  const r = res.data.result
                  message.success(`Late fees processed: ${r.charged} invoices charged, ${r.sent} sent, ${r.errors} errors`)
                } catch (e: any) { message.error(e.response?.data?.message || 'Failed to trigger late fees') }
                finally { setTriggeringLateFees(false) }
              }}
              loading={triggeringLateFees}
            >
              Trigger Late Fees
            </Button>
          </Space>
        </Card>

        <Card title="Edit Subscription">
          <Form form={subForm} layout="vertical" onFinish={handleSubSave} component={false}>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="subscriptionPlan" label="Plan">
                  <Select>
                    <Option value="starter">Starter</Option>
                    <Option value="professional">Professional</Option>
                    <Option value="enterprise">Enterprise</Option>
                    <Option value="free">Free</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="subscriptionStatus" label="Status">
                  <Select>
                    <Option value="active">Active</Option>
                    <Option value="trial">Trial</Option>
                    <Option value="expired">Expired</Option>
                    <Option value="past_due">Past Due</Option>
                    <Option value="canceled">Canceled</Option>
                    <Option value="suspended">Suspended</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="isActive" label="Business Active" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="trialEndDate" label="Trial End Date">
                  <DatePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="subscriptionEndsAt" label="Subscription Ends At">
                  <DatePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm" />
                </Form.Item>
              </Col>
            </Row>
            <Button
              type="primary"
              icon={<CalendarOutlined />}
              loading={subSaving}
              onClick={() => subForm.submit()}
            >
              Save Subscription
            </Button>
          </Form>
        </Card>

        <Card title="Stripe Billing Link" style={{ marginBottom: 16 }}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Link a Stripe subscription"
            description="Use this when a business paid in Stripe but the app is missing the subscription ID. Save the IDs manually, or use Sync from Stripe to pull status and billing dates from Stripe."
          />
          <Form form={subForm} layout="vertical" component={false}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="stripeSubscriptionId"
                  label="Stripe Subscription ID"
                  rules={[
                    {
                      validator: (_, value) => {
                        const trimmed = (value || '').trim()
                        if (!trimmed || trimmed.startsWith('sub_')) return Promise.resolve()
                        return Promise.reject(new Error('Must start with sub_'))
                      },
                    },
                  ]}
                >
                  <Input placeholder="sub_..." allowClear />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="stripeCustomerId"
                  label="Stripe Customer ID"
                  rules={[
                    {
                      validator: (_, value) => {
                        const trimmed = (value || '').trim()
                        if (!trimmed || trimmed.startsWith('cus_')) return Promise.resolve()
                        return Promise.reject(new Error('Must start with cus_'))
                      },
                    },
                  ]}
                >
                  <Input placeholder="cus_..." allowClear />
                </Form.Item>
              </Col>
            </Row>
            <Space wrap>
              <Button
                type="primary"
                icon={<LinkOutlined />}
                loading={subSaving}
                onClick={handleStripeSave}
              >
                Save Stripe IDs
              </Button>
              <Button icon={<SyncOutlined />} loading={subSaving} onClick={handleStripeSync}>
                Sync from Stripe
              </Button>
            </Space>
          </Form>
        </Card>

        <Card
          title="Send Custom Subscription Offer"
          style={{ marginBottom: 16 }}
          extra={<Text type="secondary">Bills monthly via Stripe</Text>}
        >
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Custom pricing"
            description="Define a fully custom plan (name, price, limits, optional trial). The business receives an email and will also see the offer in their app until they subscribe or the offer expires."
          />
          <Form.Item label="Copy from existing plan" style={{ marginBottom: 16 }}>
            <Select
              allowClear
              showSearch
              placeholder="Start from a catalog plan (optional)"
              value={copyFromPlanId}
              onChange={handleCopyFromPlan}
              optionFilterProp="label"
              options={catalogPlans.map((plan) => ({
                value: plan.id,
                label: `${plan.name} — $${Number(plan.basePrice || 0).toFixed(2)}/mo · ${plan.userLimit} users`,
              }))}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form
            form={offerForm}
            layout="vertical"
            onFinish={handleSendOffer}
            onFinishFailed={handleOfferValidationFailed}
            scrollToFirstError
            initialValues={{
              userLimit: 5,
              includesIntegrations: true,
              includesMobile: true,
              includesAICallAgent: false,
              expiryDays: 14,
              includeTrial: false,
              trialDays: 14,
            }}
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="name" label="Plan name" rules={[{ required: true, message: 'Required' }]}>
                  <Input placeholder="e.g. Custom Growth Plan" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="monthlyAmount" label="Monthly price ($)" rules={[{ required: true, message: 'Required' }]}>
                  <InputNumber min={1} precision={2} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="userLimit" label="User limit" rules={[{ required: true, message: 'Required' }]}>
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item name="includesIntegrations" label="Integrations" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="includesMobile" label="Mobile app" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="includesAICallAgent" label="AI call agent" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="expiryDays" label="Offer expires in (days)">
                  <InputNumber min={1} max={90} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item name="includeTrial" label="Include trial" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item noStyle shouldUpdate={(prev, next) => prev.includeTrial !== next.includeTrial}>
                  {({ getFieldValue }) =>
                    getFieldValue('includeTrial') ? (
                      <Form.Item
                        name="trialDays"
                        label="Trial period (days)"
                        rules={[{ required: true, message: 'Required when trial is enabled' }]}
                      >
                        <InputNumber min={1} max={365} style={{ width: '100%' }} />
                      </Form.Item>
                    ) : null
                  }
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="smsLimitPerMonth" label="SMS limit / month">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="Optional" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="emailLimitPerMonth" label="Email limit / month">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="Optional" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="callMinutesPerMonth" label="Call minutes / month">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="Optional" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="description" label="Description">
              <TextArea rows={2} placeholder="Optional plan description" />
            </Form.Item>
            <Form.Item
              name="adminMessage"
              label="Message to business"
              extra="Included in the offer email. Copying a plan auto-fills the feature list here — edit as needed."
            >
              <TextArea rows={6} placeholder="Optional note included in the offer email" />
            </Form.Item>
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={sendingOffer}
              onClick={() => offerForm.submit()}
            >
              Send offer
            </Button>
          </Form>

          {offerSendResult && !offerSendResult.error && (
            <Card size="small" style={{ marginTop: 16 }} title="Latest offer">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text>
                  <strong>{offerSendResult.name}</strong> — ${Number(offerSendResult.monthlyAmount).toFixed(2)}/mo
                </Text>
                {offerSendResult.checkoutUrl && (
                  <>
                    <Input.TextArea
                      value={offerSendResult.checkoutUrl}
                      readOnly
                      autoSize={{ minRows: 2, maxRows: 4 }}
                    />
                    <Button icon={<CopyOutlined />} onClick={() => copyCheckoutLink(offerSendResult.checkoutUrl)}>
                      Copy checkout link
                    </Button>
                  </>
                )}
                {offerSendResult.emailWarning && (
                  <Alert type="warning" showIcon message={offerSendResult.emailWarning} />
                )}
              </Space>
            </Card>
          )}

          {offerSendResult?.error && (
            <Alert type="error" showIcon style={{ marginTop: 16 }} message={offerSendResult.error} />
          )}
        </Card>

        <Card title="Subscription Offer History">
          {offersLoadError && (
            <Alert type="error" showIcon style={{ marginBottom: 16 }} message={offersLoadError} />
          )}
          <Table
            rowKey="id"
            loading={offersLoading}
            dataSource={offers}
            pagination={{ pageSize: 8 }}
            columns={[
              { title: 'Plan', dataIndex: 'name', key: 'name' },
              {
                title: 'Price',
                key: 'monthlyAmount',
                render: (_: unknown, row: any) => `$${Number(row.monthlyAmount).toFixed(2)}/mo`,
              },
              {
                title: 'Trial',
                key: 'trialDays',
                render: (_: unknown, row: any) =>
                  row.trialDays > 0 ? `${row.trialDays} days` : '—',
              },
              { title: 'Users', dataIndex: 'userLimit', key: 'userLimit' },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                render: (status: string) => (
                  <Tag color={status === 'accepted' ? 'green' : status === 'pending' ? 'blue' : 'default'}>
                    {status}
                  </Tag>
                ),
              },
              {
                title: 'Sent',
                dataIndex: 'sentAt',
                key: 'sentAt',
                render: (v: string | null) => (v ? dayjs(v).format('MMM D, YYYY') : '—'),
              },
              {
                title: 'Expires',
                dataIndex: 'expiresAt',
                key: 'expiresAt',
                render: (v: string | null) => (v ? dayjs(v).format('MMM D, YYYY') : '—'),
              },
              {
                title: 'Actions',
                key: 'actions',
                render: (_: unknown, row: any) => (
                  <Space size="small">
                    {row.checkoutUrl && (
                      <Button size="small" icon={<CopyOutlined />} onClick={() => copyCheckoutLink(row.checkoutUrl)}>
                        Copy link
                      </Button>
                    )}
                    {row.status === 'pending' && (
                      <>
                        <Button size="small" onClick={() => handleResendOffer(row.id)}>Resend</Button>
                        <Button size="small" danger onClick={() => handleCancelOffer(row.id)}>Cancel</Button>
                      </>
                    )}
                  </Space>
                ),
              },
            ]}
          />
        </Card>
      </div>
    )
  }

  const handleLoginAsBusiness = async () => {
    setImpersonating(true)
    try {
      const preferred =
        business?.users?.find((u) => u.isActive && u.role === 'OWNER') ||
        business?.users?.find((u) => u.isActive && ['ADMIN', 'SUPER_ADMIN'].includes(u.role)) ||
        business?.users?.find((u) => u.isActive)

      const res = await api.post(`/businesses/${id}/impersonate`, {
        userId: preferred?.id,
      })
      if (res.data?.magicLink) {
        window.open(res.data.magicLink, '_blank')
        message.success('Opened business account in a new tab')
      }
    } catch (error: any) {
      console.error('Impersonation failed:', error)
      message.error(error.response?.data?.message || 'Failed to generate impersonation link')
    } finally {
      setImpersonating(false)
    }
  }

  const eligibleReviewUsers = (business?.users || []).filter(
    (user) => user.isActive && ['OWNER', 'ADMIN'].includes(user.role)
  )

  const handleGenerateReviewLink = async () => {
    if (!reviewUserId) {
      message.warning('Please select a business user')
      return
    }

    setReviewGenerating(true)
    try {
      const res = await api.post(`/businesses/${id}/review-request`, {
        userId: reviewUserId,
        sendEmail: reviewSendEmail
      })

      if (res.data?.success && res.data?.data) {
        setReviewResult(res.data.data)
        if (id && reviewUserId) {
          sessionStorage.setItem(
            reviewLinkStorageKey(id, reviewUserId),
            JSON.stringify(res.data.data)
          )
        }
        if (res.data.data.emailSent) {
          message.success(`Review link sent to ${res.data.data.recipientEmail}`)
        } else {
          message.success('Review link generated')
        }
      }
    } catch (error: any) {
      console.error('Review link generation failed:', error)
      message.error(error.response?.data?.message || 'Failed to generate review link')
    } finally {
      setReviewGenerating(false)
    }
  }

  const handleCopyReviewLink = async () => {
    if (!reviewResult?.url) return
    try {
      await navigator.clipboard.writeText(reviewResult.url)
      message.success('Link copied to clipboard')
    } catch {
      message.error('Failed to copy link')
    }
  }

  const productReviewTab = (
    <Card title="ServiceFlow Product Review">
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Send a review link so this business can submit a testimonial for ServiceFlow Pro (stars, quote, name, title, company, and photo)."
      />
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="Business User" required>
            <Select
              placeholder="Select owner or admin"
              value={reviewUserId}
              onChange={setReviewUserId}
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="children"
            >
              {eligibleReviewUsers.map((user) => (
                <Option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName} ({user.email}) — {user.role}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Delivery">
            <Space direction="vertical">
              <Switch
                checked={reviewSendEmail}
                onChange={setReviewSendEmail}
                checkedChildren="Email user"
                unCheckedChildren="Link only"
              />
              <Text type="secondary">
                {reviewSendEmail
                  ? 'An email with the review link will be sent to the selected user.'
                  : 'Generate the link only — copy and share manually.'}
              </Text>
            </Space>
          </Form.Item>
        </Col>
      </Row>
      <Button
        type="primary"
        icon={<LinkOutlined />}
        loading={reviewGenerating}
        onClick={handleGenerateReviewLink}
        disabled={!reviewUserId}
      >
        Generate Review Link
      </Button>

      {reviewResult && (
        <Card size="small" style={{ marginTop: 24 }} title="Review Link">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Input.TextArea
              value={reviewResult.url}
              readOnly
              autoSize={{ minRows: 2, maxRows: 4 }}
            />
            <Space>
              <Button icon={<CopyOutlined />} onClick={handleCopyReviewLink}>
                Copy Link
              </Button>
              <Text type="secondary">
                Expires: {dayjs(reviewResult.expiresAt).format('MMM D, YYYY h:mm A')}
              </Text>
              {reviewResult.emailSent && reviewResult.recipientEmail && (
                <Tag color="green">Emailed to {reviewResult.recipientEmail}</Tag>
              )}
            </Space>
          </Space>
        </Card>
      )}
    </Card>
  )

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/businesses')}
          >
            Back to Businesses
          </Button>
          <Button
            type="primary"
            icon={<LoginOutlined />}
            loading={impersonating}
            onClick={handleLoginAsBusiness}
          >
            Login as Business
          </Button>
        </div>
        
        <Title level={2}>{business.name} - Settings</Title>
        <Text type="secondary">
          Configure integrations and settings for this business
        </Text>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        initialValues={settings}
      >
        <Tabs
          defaultActiveKey="subscription"
          items={[
            {
              key: 'users',
              label: (
                <span>
                  <UserOutlined /> Users
                </span>
              ),
              children: id ? (
                <BusinessUsersTab
                  businessId={id}
                  businessName={business?.name}
                  onUsersChanged={fetchBusiness}
                />
              ) : null,
            },
            {
              key: 'subscription',
              label: 'Subscription & Trial',
              children: <SubscriptionTab />
            },
            {
              key: 'paid-addons',
              label: 'Paid Add-ons',
              children: <PaidAddonsTab />
            },
            {
              key: 'product-review',
              label: (
                <span>
                  <StarOutlined /> Product Review
                </span>
              ),
              children: productReviewTab
            },
            {
              key: 'stripe',
              label: 'Stripe',
              children: stripeConfigTab
            },
            {
              key: 'helcim',
              label: 'Helcim',
              children: helcimConfigTab
            },
            {
              key: 'integrations',
              label: 'Integrations',
              children: integrationsTab
            },
            {
              key: 'notifications',
              label: 'Notifications',
              children: notificationsTab
            },
            {
              key: 'branding',
              label: 'Branding',
              children: brandingTab
            },
            {
              key: 'features',
              label: 'Features',
              children: featuresTab
            },
            {
              key: 'raw-json',
              label: 'Raw JSON Editor',
              children: rawJsonTab
            }
          ]}
        />

        <Divider />

        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button onClick={() => navigate('/businesses')}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType={useRawJson ? 'button' : 'submit'}
              icon={<SaveOutlined />}
              loading={saving}
              onClick={useRawJson ? handleSaveClick : undefined}
            >
              Save Settings
            </Button>
          </Space>
        </div>
      </Form>
    </div>
  )
}

export default BusinessSettingsPage
